import { Button, Placeholder, Text } from "@telegram-apps/telegram-ui";
import { openInvoice } from "@telegram-apps/sdk";
import React, { type FC, useEffect, useState, useCallback } from "react";
import ApiService from "../../handlers/apiService";
import {
  BoostTypeEnum,
  PackIdEnum,
  CoinBundleIdEnum,
} from "../../handlers/types";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { useServices } from "../../services/ServicesProvider";
import "./Shop.css";
import StarIcon from "./StarIcon";

// Only include boosts that are displayed in the shop
const BOOST_IMAGES: Partial<Record<BoostTypeEnum, string>> = {
  [BoostTypeEnum.Revive]: "/assets/img/ui/revive.webp",
  [BoostTypeEnum.Explosion]: "/assets/img/ui/explosion.webp",
  [BoostTypeEnum.Heal]: "/assets/img/ui/heal.webp",
};

const PACK_IMAGES: Record<PackIdEnum, string> = {
  [PackIdEnum.BASICBOOSTS3]: "/assets/img/ui/bundle.webp",
  [PackIdEnum.BASICBOOSTS6]: "/assets/img/ui/bundle.webp",
  [PackIdEnum.BASICBOOSTS9]: "/assets/img/ui/bundle.webp",
  [PackIdEnum.HEAL10]: "/assets/img/ui/heal.webp",
  [PackIdEnum.REVIVE10]: "/assets/img/ui/revive.webp",
  [PackIdEnum.EXPLOSION10]: "/assets/img/ui/explosion.webp",
};

const COIN_BUNDLE_IMAGES: Record<CoinBundleIdEnum, string> = {
  [CoinBundleIdEnum.COINS1K]: "/assets/img/coins/1.avif",
  [CoinBundleIdEnum.COINS2K]: "/assets/img/coins/2.avif",
  [CoinBundleIdEnum.COINS5K]: "/assets/img/coins/3.avif",
  [CoinBundleIdEnum.COINS10K]: "/assets/img/coins/4.avif",
  [CoinBundleIdEnum.COINS20K]: "/assets/img/coins/5.avif",
  [CoinBundleIdEnum.COINS50K]: "/assets/img/coins/6.avif",
  [CoinBundleIdEnum.COINS100K]: "/assets/img/coins/7.avif",
  [CoinBundleIdEnum.COINS300K]: "/assets/img/coins/8.avif",
  [CoinBundleIdEnum.COINS1M]: "/assets/img/coins/9.avif",
};

interface ShopItem {
  type: BoostTypeEnum;
  price: number;
  inInventory: number;
}

interface PackItem {
  id: PackIdEnum;
  nameKey: string;
  price: number;
  discountPrice?: number;
  items: {
    boosts?: {
      heal?: number;
      revive?: number;
      explosion?: number;
      doubling?: number;
      energy_one?: number;
      energy_full?: number;
    };
    looks?: string[];
  };
}

interface CoinBundleItem {
  id: CoinBundleIdEnum;
  nameKey: string;
  price: number;
  discountPrice?: number;
  amount: number;
  extraAmount?: number;
}

function useShopItems() {
  const { t } = useI18n();
  const {} = useServices();
  const [items, setItems] = useState<ShopItem[]>([]);
  const [packs, setPacks] = useState<PackItem[]>([]);
  const [coinBundles, setCoinBundles] = useState<CoinBundleItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);

  const fetchItems = useCallback(
    async (isRefetch = false) => {
      if (isRefetch) {
        setIsRefetching(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const response = await ApiService.getInstance().api.purchasesList();

        // Transform the boosts response to match ShopItem interface
        const shopItems: ShopItem[] = response.boosts
          // Filter out energy-related boost types
          .filter(
            (boost) =>
              boost.type !== BoostTypeEnum.EnergyOne &&
              boost.type !== BoostTypeEnum.EnergyFull &&
              boost.type !== BoostTypeEnum.Doubling // Also exclude doubling boost
          )
          .map((boost) => ({
            type: boost.type,
            price: boost.price,
            inInventory: boost.count,
          }));

        // Transform the packs response to match PackItem interface
        const packItems: PackItem[] = response.packs.map((pack) => ({
          id: pack.id,
          nameKey: pack.nameKey,
          price: pack.price,
          discountPrice: pack.discountPrice,
          items: pack.items,
        }));

        // Transform the coin bundles response to match CoinBundleItem interface
        const coinBundleItems: CoinBundleItem[] = response.coinBundles.map(
          (bundle) => ({
            id: bundle.id,
            nameKey: bundle.nameKey,
            price: bundle.price,
            discountPrice: bundle.discountPrice,
            amount: bundle.amount,
            extraAmount: bundle.extraAmount,
          })
        );

        setItems(shopItems);
        setPacks(packItems);
        setCoinBundles(coinBundleItems);

        // Fresh looks data handling removed since character customization is disabled
      } catch (err) {
        setError(t("toolkit.shop.errorLoadingItems"));
      } finally {
        setIsLoading(false);
        setIsRefetching(false);
      }
    },
    [t]
  );

  const refetch = useCallback(() => fetchItems(true), [fetchItems]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return {
    items,
    packs,
    coinBundles,
    isLoading,
    isRefetching,
    error,
    refetch,
  };
}

function usePurchase(onSuccess: () => void) {
  const { t } = useI18n();
  const [isPurchasing, setIsPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const purchaseBoost = useCallback(
    async (boostType: BoostTypeEnum) => {
      setIsPurchasing(boostType);
      setError(null);

      try {
        const { invoiceUrl } =
          await ApiService.getInstance().api.purchasesBuyCreate({
            boostType,
          });

        if (invoiceUrl && openInvoice.isAvailable()) {
          const status = await openInvoice(invoiceUrl, "url");
          if (status !== "paid") {
            setError(t("toolkit.shop.errorPayment"));
          } else {
            // Wait for 1 second before updating
            await new Promise((resolve) => setTimeout(resolve, 1000));
            onSuccess();
          }
        }
      } catch (err) {
        setError(t("toolkit.shop.errorPurchase"));
      } finally {
        setIsPurchasing(null);
      }
    },
    [onSuccess, t]
  );

  const purchasePack = useCallback(
    async (packId: PackIdEnum) => {
      setIsPurchasing(packId);
      setError(null);

      try {
        const { invoiceUrl } =
          await ApiService.getInstance().api.purchasesBuyCreate({
            packId,
          });

        if (invoiceUrl && openInvoice.isAvailable()) {
          const status = await openInvoice(invoiceUrl, "url");
          if (status !== "paid") {
            setError(t("toolkit.shop.errorPayment"));
          } else {
            // Wait for 1 second before updating
            await new Promise((resolve) => setTimeout(resolve, 1000));
            onSuccess();
          }
        }
      } catch (err) {
        setError(t("toolkit.shop.errorPurchase"));
      } finally {
        setIsPurchasing(null);
      }
    },
    [onSuccess, t]
  );

  const purchaseCoinBundle = useCallback(
    async (coinBundleId: CoinBundleIdEnum) => {
      setIsPurchasing(coinBundleId);
      setError(null);

      try {
        const { invoiceUrl } =
          await ApiService.getInstance().api.purchasesBuyCreate({
            coinBundleId,
          });

        if (invoiceUrl && openInvoice.isAvailable()) {
          const status = await openInvoice(invoiceUrl, "url");
          if (status !== "paid") {
            setError(t("toolkit.shop.errorPayment"));
          } else {
            // Wait for 1 second before updating
            await new Promise((resolve) => setTimeout(resolve, 1000));
            onSuccess();
          }
        }
      } catch (err) {
        setError(t("toolkit.shop.errorPurchase"));
      } finally {
        setIsPurchasing(null);
      }
    },
    [onSuccess, t]
  );

  return {
    purchaseBoost,
    purchasePack,
    purchaseCoinBundle,
    isPurchasing,
    error,
  };
}

export const Shop: FC = () => {
  const { t } = useI18n();
  const {
    items,
    packs,
    coinBundles,
    isLoading,
    isRefetching,
    error: fetchError,
    refetch,
  } = useShopItems();
  const {
    purchaseBoost,
    purchasePack,
    purchaseCoinBundle,
    isPurchasing,
    error: purchaseError,
  } = usePurchase(refetch);

  if (isLoading) {
    return (
      <Placeholder
        header={t("toolkit.shop.loadingHeader")}
        description={t("toolkit.shop.loadingDescription")}
      />
    );
  }

  if (fetchError) {
    return (
      <Placeholder
        header={t("common.error")}
        description={fetchError}
        action={
          <Button onClick={() => refetch()}>{t("common.tryAgain")}</Button>
        }
      />
    );
  }

  return (
    <div className="shop-page">
      <div className="shop-header">
        <h1 className="shop-title">{t("toolkit.shop.title")}</h1>
        <p className="shop-subtitle">{t("toolkit.shop.subtitle")}</p>
      </div>

      {isRefetching && (
        <Text className="shop-page__status">
          {t("toolkit.shop.refreshing")}
        </Text>
      )}

      {purchaseError && (
        <Text className="shop-page__error" color="danger">
          {purchaseError}
        </Text>
      )}

      <div className="shop-section">
        <h2 className="shop-section-title">
          {t("toolkit.shop.section.coinBundles")}
        </h2>
        <div className="shop-cards">
          {coinBundles.map((bundle, index) => {
            // Get translated title and description with fallbacks for unknown types
            const title = t(`toolkit.shop.coinBundle.${bundle.id}`, {
              fallback: bundle.nameKey,
            });

            // Use server-provided extra amount for bonus display
            const description =
              bundle.extraAmount && bundle.extraAmount > 0
                ? `+${bundle.extraAmount.toLocaleString()}`
                : "";

            return (
              <div
                key={index}
                className={`shop-card ${
                  isPurchasing === bundle.id ? "shop-card--loading" : ""
                }`}
                onClick={() =>
                  isPurchasing === null ? purchaseCoinBundle(bundle.id) : null
                }
                style={{
                  cursor: isPurchasing === null ? "pointer" : "default",
                }}
              >
                <div className="card-content">
                  <div className="card-title card-title--coin-bundle">
                    {title}
                  </div>

                  <img
                    src={COIN_BUNDLE_IMAGES[bundle.id]}
                    alt={bundle.id}
                    className="card-image card-image--coin-bundle"
                  />

                  <p className="card-subtitle card-subtitle--coin-bonus">
                    {description}
                  </p>
                </div>

                <div className="card-price-button">
                  <div className="card-price">
                    {isPurchasing === bundle.id ? (
                      <span className="card-loader"></span>
                    ) : bundle.discountPrice ? (
                      <>
                        <span className="card-price--original">
                          {bundle.price}
                        </span>
                        &nbsp;
                        <span>{bundle.discountPrice}</span>
                      </>
                    ) : (
                      <span>{bundle.price}</span>
                    )}
                    <StarIcon />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="shop-section">
        <h2 className="shop-section-title">
          {t("toolkit.shop.section.packs")}
        </h2>
        <div className="shop-cards">
          {packs.map((pack, index) => {
            // Get translated title and description with fallbacks for unknown types
            const title = t(`toolkit.shop.pack.${pack.id}`, {
              fallback: pack.nameKey,
            });
            const description = t(`toolkit.shop.pack.description.${pack.id}`, {
              fallback: "",
            });

            return (
              <div
                key={index}
                className={`shop-card ${
                  isPurchasing === pack.id ? "shop-card--loading" : ""
                }`}
                onClick={() =>
                  isPurchasing === null ? purchasePack(pack.id) : null
                }
                style={{
                  cursor: isPurchasing === null ? "pointer" : "default",
                }}
              >
                <div className="card-content">
                  <div className="card-title">{title}</div>

                  <img
                    src={PACK_IMAGES[pack.id]}
                    alt={pack.id}
                    className="card-image"
                  />

                  <p className="card-subtitle">{description}</p>
                </div>

                <div className="card-price-button">
                  <div className="card-price">
                    {isPurchasing === pack.id ? (
                      <span className="card-loader"></span>
                    ) : pack.discountPrice ? (
                      <>
                        <span className="card-price--original">
                          {pack.price}
                        </span>
                        &nbsp;
                        <span>{pack.discountPrice}</span>
                      </>
                    ) : (
                      <span>{pack.price}</span>
                    )}
                    <StarIcon />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="shop-section">
        <h2 className="shop-section-title">
          {t("toolkit.shop.section.boosts")}
        </h2>
        <div className="shop-cards">
          {items.map((item, index) => {
            // Get translated title and description with fallbacks for unknown types
            const title = t(`toolkit.shop.item.${item.type}`, {
              fallback: item.type,
            });
            const description = t(
              `toolkit.shop.item.description.${item.type}`,
              {
                fallback: "",
              }
            );

            return (
              <div
                key={index}
                className={`shop-card ${
                  isPurchasing === item.type ? "shop-card--loading" : ""
                }`}
                onClick={() =>
                  isPurchasing === null ? purchaseBoost(item.type) : null
                }
                style={{
                  cursor: isPurchasing === null ? "pointer" : "default",
                }}
              >
                <div className="card-number">{item.inInventory}</div>
                <div className="card-content">
                  <div className="card-title">{title}</div>

                  <img
                    src={BOOST_IMAGES[item.type] || ""}
                    alt={item.type}
                    className="card-image"
                  />

                  <p className="card-subtitle">{description}</p>
                </div>

                <div className="card-price-button">
                  <div className="card-price">
                    {isPurchasing === item.type ? (
                      <span className="card-loader"></span>
                    ) : (
                      <span>{item.price}</span>
                    )}
                    <StarIcon />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="shop-section">
        <h2 className="shop-section-title">
          {t("toolkit.shop.section.looks")}
        </h2>
        <div className="shop-looks-container">
          <div className="shop-looks-placeholder">
            Character customization coming soon!
          </div>
        </div>
      </div>
    </div>
  );
};
